import { memo, useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setPopupMessageModel } from '../../../redux/app/reducer'
import ANTDButton from '../../../shared/antd/ANTDButton'
import { ANTDDateRange } from '../../../shared/antd/ANTDDatePicker'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import ANTDTable from '../../../shared/antd/ANTDTable'
import Label from '../../../shared/Label'
import { apiParams } from '../../../utils'
import { get30Days, numberFormatting } from '../../../utils/customFunctions'
import { formatDate, SAVE_FORMAT } from '../../../utils/dayjs'
import { isEqual, length } from '../../../utils/javascript'
import FiscalYearSelect from '../../common/presentation/FiscalYearSelect'
import ExportColumn from '../../jobs/presentation/common/ExportColumn'
import ViewExcelHistory from '../../jobs/presentation/common/ViewExcelHistory'
import { getTagListApi } from '../../userManagement/user.api'
import {
  exportToExcelApi,
  getColumnListApi,
  getReportHistoryListApi,
  getReportListApi,
} from '../report.api'

const InventoryTable = ({ activeTab }) => {
  const { t } = useTranslations()
  const { dispatch, selector } = useRedux()
  const { dateRange } = selector(state => state?.app?.fiscalYear)

  const [loading, setLoading] = useState(false)
  const [bucketsList, setBucketsList] = useState([])
  const [filterBucket, setFilterBucket] = useState(null)
  const [selectedBucket, setSelectedBucket] = useState(null)
  const [reportData, setReportData] = useState({ loader: false })
  const [viewReportHistory, setViewReportHistory] = useState({ open: false })

  const [excelColumn, setExcelColumn] = useState([])
  const { fromDate, toDate } = get30Days()
  const [filterDate, setFilterDate] = useState({
    fromDate,
    toDate,
  })
  const [selectExportColModal, setSelectExportColModal] = useState({
    open: false,
    selectedColumn: [],
  })
  const saveFormat = 'DD/MM/YYYY'

  useEffect(() => {
    const getBucketName = async () => {
      const params = {
        type: 'BUCKET_NAME',
      }
      const response = await getTagListApi({ params })
      if (length(response?.data?.list)) {
        setBucketsList(
          response?.data?.list?.map(value => ({
            value: value?.id,
            label: t(value?.key || value?.name),
          })),
        )
        const defaultSelectedBucketId = response?.data?.list?.[0]?.id

        setSelectedBucket(defaultSelectedBucketId)
        setFilterBucket(defaultSelectedBucketId)
        columnListApi()
      }
    }
    getBucketName()
  }, [])

  useEffect(() => {
    if (selectedBucket && dateRange && isEqual(activeTab, 'inventoryTable')) {
      getReportListFn()
    }
  }, [selectedBucket, dateRange])

  const columnListApi = async () => {
    const response = await getColumnListApi({
      params: apiParams({ params: { sheetType: 'BUCKET_PERIOD_SHEET' } }),
    })
    if (response?.data) {
      setExcelColumn(response?.data?.list)
      setSelectExportColModal(prev => ({
        ...prev,
        selectedColumn: response?.data?.list?.map(value => value?.id) || [],
      }))
    }
  }

  const getReportListFn = async ({ pageNo = 1, from, to } = {}) => {
    if (selectedBucket) {
      const payload = {
        bucketNameId: selectedBucket,
        fromDate: from || dateRange?.from,
        toDate: to || dateRange?.to,
      }
      setReportData(prev => ({ ...prev, loader: true }))
      const params = apiParams({ params: payload, pageNo })
      const response = await getReportListApi({ params })
      if (response?.data) {
        setReportData(response?.data)
      }
      setReportData(prev => ({ ...prev, loader: false }))
    }
  }

  const reportHistoryAPI = async ({ pageNo = 1 } = {}) => {
    const payload = {
      sheetType: 'BUCKET_PERIOD_SHEET',
    }
    setViewReportHistory(prev => ({ ...prev, loader: true }))
    try {
      const response = await getReportHistoryListApi({
        params: apiParams({ pageNo, params: payload }),
      })
      if (response?.data) {
        return response?.data
      }
    } catch (error) {
    } finally {
      setViewReportHistory(prev => ({ ...prev, loader: false }))
    }
  }

  const onViewReportHistory = async ({ closeModal = false }) => {
    const data = !closeModal && (await reportHistoryAPI())
    setViewReportHistory(prev => ({
      ...prev,
      open: !prev?.open,
      data: data || {},
    }))
  }

  const onHistoryTableChange = async pagination => {
    const data = await reportHistoryAPI({ pageNo: pagination?.current })
    setViewReportHistory(prev => ({ ...prev, data: data || {} }))
  }

  const onSelectAllColumn = e => {
    setSelectExportColModal(prev => ({
      ...prev,
      selectedColumn: e.target.checked
        ? excelColumn?.map(value => value?.id)
        : [],
    }))
  }

  const onSelectChange = values => {
    setSelectedBucket(values)
  }

  const handleTableChange = pagination => {
    getReportListFn({ pageNo: pagination.current })
  }

  const handleDateRangeChange = ([start, end]) => {
    const fromDate = start?.format(saveFormat)
    const toDate = end?.format(saveFormat)
    setFilterDate({ fromDate, toDate })
  }

  const onExportToExcel = async () => {
    setLoading(true)
    const payload = {
      type: 'BUCKET_PERIOD_SHEET',
      columns: selectExportColModal?.selectedColumn || [],
      from: filterDate.fromDate,
      to: filterDate.toDate,
      bucketNameId: filterBucket,
    }

    const response = await exportToExcelApi({ payload })
    if (response?.data?.success) {
      dispatch(
        setPopupMessageModel({
          open: true,
          message: response?.data?.message,
          success: true,
        }),
      )
    }

    setLoading(false)
  }

  const onSelectExportCol = () => {
    setSelectExportColModal(prev => ({ ...prev, open: !prev.open }))
  }

  const onSelectColumn = list => {
    setSelectExportColModal(prev => ({ ...prev, selectedColumn: list }))
  }

  const selectBucketProps = {
    value: selectedBucket,
    options: bucketsList?.map(({ label, value }) => ({
      label: t(label),
      value,
    })),
    onChange: onSelectChange,
    showSearch: true,
    optionFilterProp: 'label',
  }

  const filterBucketProps = {
    value: filterBucket,
    options: bucketsList?.map(({ label, value }) => ({
      label: t(label),
      value,
    })),
    onChange: values => {
      setFilterBucket(values)
    },
    showSearch: true,
    optionFilterProp: 'label',
  }

  const columns = [
    {
      title: t('inv_Period'),
      dataIndex: 'period',
      key: 'period',
      width: 100,
    },
    {
      title: t('rpt_Round'),
      dataIndex: 'round',
      key: 'round',
      width: 100,
    },
    {
      title: t('rpt_ActualNumberOfELVsLinkedToThisRound'),
      dataIndex: 'elvsLinkedToRound',
      key: 'elvsLinkedToRound',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_OpeningAsPerBooksActual'),
      dataIndex: 'openingAsBooks',
      key: 'openingAsBooks',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('tag_ProcessedActual'),
      dataIndex: 'processed',
      key: 'processed',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_Dispatched'),
      dataIndex: 'dispatched',
      key: 'dispatched',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_RoundExcessDeficit'),
      dataIndex: 'roundExcess',
      key: 'roundExcess',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_ClosingAsPerBooksActual'),
      dataIndex: 'closingAsBooks',
      key: 'closingAsBooks',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_PhysicalInventoryLeftAtTheEndOfThePeriod'),
      dataIndex: 'physicalInventoryLeftAtPeriodEnd',
      key: 'physicalInventoryLeftAtPeriodEnd',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_PeriodExcessDeficitAsPerBooksActual'),
      dataIndex: 'periodExcessAsBooks',
      key: 'periodExcessAsBooks',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_MismatchError'),
      dataIndex: 'mismatch',
      key: 'mismatch',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_FIFOELVs'),
      dataIndex: 'fifoElvs',
      key: 'fifoElvs',
      render: rowData => numberFormatting(rowData) || '-',
    },
    // {
    //   title: t('rpt_RoundELVs'),
    //   dataIndex: 'roundElvs',
    //   key: 'roundElvs',
    //   render: rowData => rowData || '-',
    // },
    {
      title: t('rpt_LeftOverElvs'),
      dataIndex: 'leftOverElv',
      key: 'leftOverElv',
      render: rowData => numberFormatting(rowData) || '-',
    },
    {
      title: t('rpt_Reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: rowData => rowData || '-',
    },
  ]

  return (
    <>
      <div className="filter-actions">
        <div className="filter-actions-container">
          <div className="d-flex select-wrapper w-100">
            <ANTDDateRange
              className="date-range mb-10 flex-1 w-100"
              format={SAVE_FORMAT}
              onChange={handleDateRangeChange}
              value={[
                formatDate(filterDate?.fromDate, saveFormat),
                formatDate(filterDate?.toDate, saveFormat),
              ]}
            />
            <ANTDSelect
              className="filter-select mb-10 flex-1"
              {...filterBucketProps}
            />
          </div>

          <div className="d-flex btn-wrapper w-100">
            <ANTDButton
              className="action-button mb-10"
              type="primary"
              onClick={onSelectExportCol}
            >
              {t('job_SelectColumnsForExport')}
            </ANTDButton>
            <ANTDButton
              className="action-button mb-10"
              type="primary"
              onClick={onExportToExcel}
              loading={loading}
            >
              {t('inv_ExportToExcel')}
            </ANTDButton>
            <ANTDButton
              className="action-button mb-10"
              type="primary"
              onClick={onViewReportHistory}
            >
              {t('rpt_ViewPreviousRequest')}
            </ANTDButton>
          </div>
        </div>
      </div>

      <div className="d-flex flex-end mb-10">
        <FiscalYearSelect
          onDateChange={(from, to) => getReportListFn({ from, to })}
        />
      </div>

      <div style={{ width: '100%', textAlign: 'left' }}>
        <Label text={t('job_BucketName')} />
        <ANTDSelect className="w-100 mt-5" {...selectBucketProps} />
      </div>
      <div className="mt-20">
        <ANTDTable
          loading={reportData?.loader}
          columns={columns}
          dataSource={
            length(reportData?.list)
              ? reportData?.list?.map(val => ({ ...val, key: val?.id }))
              : []
          }
          onChange={handleTableChange}
          pagination={{
            lastFetched: reportData?.pageNo,
            current: reportData?.pageNo,
            pageSize: 10,
            total: reportData?.lastPage * 10,
            responsive: true,
          }}
          scroll={{
            x: 1600,
          }}
        />
      </div>
      <ExportColumn
        {...{
          selectExportColModal,
          onSelectExportCol,
          onSelectAllColumn,
          onSelectColumn,
          excelColumn,
          loading,
        }}
      />
      <ViewExcelHistory
        {...{ viewReportHistory, onViewReportHistory, onHistoryTableChange }}
      />
    </>
  )
}

export default memo(InventoryTable)
